import barba from '@barba/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Performance optimization settings
const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const optimizeForPerformance = (element) => {
    element.style.willChange = 'transform, opacity';
    element.style.transform = element.style.transform || 'translateZ(0)'; // Force GPU layer
};

const hasTargets = (targets) => {
    return Boolean(targets && (!('length' in targets) || targets.length > 0));
};

export function initializePageTransitions() {
    barba.init({
        transitions: [{
            name: 'enhanced-transition',
            leave(data) {
                return gsap.timeline()
                    .to(data.current.container, {
                        opacity: 0,
                        y: 50,
                        duration: 0.4,
                        ease: 'power2.out'
                    });
            },
            enter(data) {
                gsap.set(data.next.container, { opacity: 0, y: 50 });
                return gsap.timeline()
                    .to(data.next.container, {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: 'power2.out'
                    });
            },
            once(data) {
                // Initial load animations
                initializeInvestAnimations(data.next.container);
            }
        }]
    });

    // Invest page specific animations
    const initializeInvestAnimations = (container) => {
        // Check if we're on the invest page
        if (!container.querySelector('#impact')) return;

        // Floating particle system for ocean theme
        const createParticleSystem = (section) => {
            const particleCount = 20;
            const particles = [];
            const particleContainer = document.createElement('div');
            particleContainer.style.position = 'absolute';
            particleContainer.style.top = '0';
            particleContainer.style.left = '0';
            particleContainer.style.width = '100%';
            particleContainer.style.height = '100%';
            particleContainer.style.pointerEvents = 'none';
            particleContainer.style.overflow = 'hidden';
            section.style.position = 'relative';
            section.appendChild(particleContainer);

            for (let i = 0; i < particleCount; i++) {
                const particle = document.createElement('div');
                particle.style.position = 'absolute';
                particle.style.width = `${Math.random() * 6 + 2}px`;
                particle.style.height = particle.style.width;
                particle.style.background = `rgba(59, 130, 246, ${Math.random() * 0.5 + 0.2})`;
                particle.style.borderRadius = '50%';
                particle.style.left = `${Math.random() * 100}%`;
                particle.style.top = `100%`;
                particleContainer.appendChild(particle);
                particles.push(particle);
            }

            // Animate particles (bubbles rising)
            particles.forEach((particle, index) => {
                gsap.to(particle, {
                    y: `-120%`,
                    x: `+=${Math.random() * 100 - 50}px`,
                    duration: Math.random() * 5 + 5,
                    repeat: -1,
                    delay: Math.random() * 2,
                    ease: 'none',
                    onComplete: () => {
                        particle.style.top = '100%';
                        particle.style.left = `${Math.random() * 100}%`;
                    }
                });
            });
        };

        // Add particle systems to key sections
        const mainContainer = container.querySelector('.min-h-screen');
        if (mainContainer) {
            createParticleSystem(mainContainer);
        }

        const impactSection = container.querySelector('#impact');
        if (impactSection) {
            createParticleSystem(impactSection);

            ScrollTrigger.create({
                trigger: impactSection,
                start: 'top 80%',
                onEnter: () => {
                    const impactCards = impactSection.querySelectorAll('.glass-card');
                    if (!hasTargets(impactCards)) return;
                    gsap.fromTo(impactCards,
                        {
                            opacity: 0,
                            y: 60,
                            scale: 0.9,
                            rotationX: -15
                        },
                        {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            rotationX: 0,
                            duration: 0.8,
                            stagger: 0.15,
                            ease: 'power3.out'
                        }
                    );
                }
            });

            // Enhanced floating effect for impact cards
            impactSection.querySelectorAll('.glass-card').forEach((card, index) => {
                gsap.to(card, {
                    y: -10,
                    rotation: 2,
                    duration: 3 + index * 0.5,
                    repeat: -1,
                    yoyo: true,
                    ease: 'power2.inOut'
                });
            });
        }

        // Roadmap section animations
        const roadmapSection = container.querySelector('#roadmap');
        if (roadmapSection) {
            createParticleSystem(roadmapSection);

            // Animate roadmap steps on scroll
            ScrollTrigger.create({
                trigger: roadmapSection,
                start: 'top 80%',
                onEnter: () => {
                    if (prefersReducedMotion()) return;
                    const roadmapSteps = roadmapSection.querySelectorAll('.step');
                    if (!hasTargets(roadmapSteps)) return;
                    roadmapSteps.forEach(optimizeForPerformance);
                    gsap.fromTo(roadmapSteps,
                        { scale: 0.8, opacity: 0.5 },
                        {
                            scale: 1,
                            opacity: 1,
                            duration: 0.6,
                            stagger: 0.2,
                            ease: 'back.out(1.7)'
                        }
                    );
                }
            });

            // Enhanced phase transitions
            const steps = roadmapSection.querySelectorAll('.step[data-phase]');
            steps.forEach(step => {
                step.addEventListener('click', () => {
                    const phaseId = step.dataset.phase;
                    const currentPhase = roadmapSection.querySelector('.roadmap-phase:not(.hidden)');
                    const newPhase = roadmapSection.querySelector(`#${phaseId}`);

                    if (currentPhase) {
                        gsap.to(currentPhase, {
                            opacity: 0,
                            scale: 0.95,
                            duration: 0.4,
                            ease: 'power2.in',
                            onComplete: () => {
                                currentPhase.classList.add('hidden');
                            }
                        });
                    }

                    if (newPhase) {
                        newPhase.classList.remove('hidden');
                        gsap.fromTo(newPhase,
                            { opacity: 0, scale: 1.05, y: 30 },
                            {
                                opacity: 1,
                                scale: 1,
                                y: 0,
                                duration: 0.6,
                                ease: 'power2.out'
                            }
                        );
                    }

                    // Update step styles
                    steps.forEach(s => s.classList.remove('step-info', 'active'));
                    step.classList.add('step-info', 'active');
                });
            });

            // Initialize first phase with animation
            const firstPhase = roadmapSection.querySelector('#phase1');
            if (firstPhase && firstPhase.classList.contains('block')) {
                gsap.fromTo(firstPhase,
                    { opacity: 0, scale: 0.9 },
                    { opacity: 1, scale: 1, duration: 0.8, ease: 'power2.out' }
                );
            }
        }

        // Enhanced hover effects for all cards
        const allCards = container.querySelectorAll('.glass-card');
        allCards.forEach(card => {
            card.addEventListener('mouseenter', () => {
                gsap.to(card, {
                    scale: 1.02,
                    y: -5,
                    boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)',
                    duration: 0.3,
                    ease: 'power2.out'
                });
            });

            card.addEventListener('mouseleave', () => {
                gsap.to(card, {
                    scale: 1,
                    y: 0,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    duration: 0.3,
                    ease: 'power2.out'
                });
            });
        });

        // Background morphing shapes for invest page
        if (container.querySelector('.min-h-screen')) {
            gsap.to('.min-h-screen', {
                background: 'linear-gradient(45deg, #1e3a8a 0%, #3b82f6 25%, #06b6d4 50%, #1d4ed8 75%, #1e40af 100%)',
                backgroundSize: '400% 400%',
                duration: 20,
                repeat: -1,
                ease: 'none',
                onUpdate: function() {
                    this.targets()[0].style.backgroundPosition = `${(this.progress() * 400)}% ${Math.sin(this.time()) * 50 + 50}%`;
                }
            });
        }

        // Animated counters for opportunity section
        const counters = container.querySelectorAll('[data-target]');
        counters.forEach(counter => {
            ScrollTrigger.create({
                trigger: counter,
                start: 'top 80%',
                onEnter: () => {
                    if (prefersReducedMotion()) {
                        counter.innerText = counter.getAttribute('data-target');
                        return;
                    }
                    const target = parseFloat(counter.getAttribute('data-target'));
                    const duration = 2;
                    let start = 0;
                    const increment = target / (duration * 60); // 60 FPS

                    const tl = gsap.timeline();
                    tl.to({}, { duration: duration, onUpdate: function() {
                        start += increment;
                        if (start >= target) start = target;
                        counter.innerText = target > 100 ? Math.floor(start) : start.toFixed(1);
                    }});
                }
            });
        });
    };

    // Add hover effects and animations
    const initializeAnimations = () => {
        // Card hover animations
        const cards = document.querySelectorAll('.hover\\:scale-115');
        
        cards.forEach(card => {
            card.addEventListener('mouseover', () => {
                gsap.to(card, {
                    scale: 1.05,
                    duration: 0.3,
                    ease: 'power2.out'
                });
            });
            
            card.addEventListener('mouseout', () => {
                gsap.to(card, {
                    scale: 1,
                    duration: 0.3,
                    ease: 'power2.out'
                });
            });
        });

        // Initial animation for hero section
        const heroText = document.querySelector('.text-6xl');
        if (heroText) {
            gsap.from(heroText, {
                opacity: 0,
                y: -50,
                duration: 1,
                ease: 'power3.out'
            });
        }

        // Only animate non-instruction card grid items
        const gridItems = document.querySelectorAll('.grid-cols-1 > div:not(.card)');
        if (hasTargets(gridItems)) {
            gsap.from(gridItems, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                stagger: 0.1,
                ease: 'power3.out'
            });
        }

    };

    // Initialize animations on page load and after Barba transitions
    document.addEventListener('DOMContentLoaded', () => {
        initializeAnimations();
        initializeInvestAnimations(document.body);
    });
    barba.hooks.after(() => {
        ScrollTrigger.refresh();
        initializeInvestAnimations(document.body);
    });

    // Handle window resize for responsive animations
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 250);
    });

    // Optimize for mobile devices
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(registration => registration.unregister());
        });
    }

    // Handle navigation and internal section transitions
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;

        const currentUrl = new URL(window.location.href);
        const linkUrl = new URL(link.href, window.location.href);
        const currentPath = currentUrl.pathname.replace(/\/$/, '');
        const linkPath = linkUrl.pathname.replace(/\/$/, '');
        const isSamePageHash =
            linkUrl.origin === currentUrl.origin &&
            linkPath === currentPath &&
            linkUrl.search === currentUrl.search &&
            Boolean(linkUrl.hash);

        // Check for internal anchor links (same page section navigation)
        if (isSamePageHash) {
            e.preventDefault();
            const targetId = linkUrl.hash.substring(1);
            const targetSection = document.getElementById(targetId);
            
            if (targetSection) {
                // Smooth scroll animation to section
                gsap.to(window, {
                    duration: 1.2,
                    scrollTo: {
                        y: targetSection,
                        offsetY: 80, // Account for header height
                        autoKill: true
                    },
                    ease: 'power2.inOut'
                });

                // Animate section reveal
                gsap.fromTo(targetSection,
                    { opacity: 0.8, scale: 0.98 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.6,
                        ease: 'power2.out'
                    }
                );

                // Update active nav state
                const navLinks = document.querySelectorAll('nav a');
                navLinks.forEach(navLink => navLink.classList.remove('active'));
                link.classList.add('active');
                history.replaceState(null, '', linkUrl.hash);
            }
            return;
        }

        // External Barba navigation
        if (link.href.startsWith(window.location.origin) && !link.dataset.barbaPrevent) {
            e.preventDefault();
            barba.go(link.href);
        }
    });

}
